import React from 'react'
import styles from './AdminPage.scss'
import {connect} from 'react-redux'
import {loadReports, updateReport, saveReport} from 'lib/api/rideReports'
import {Icon} from 'common/LegacyIcon'
import appStyles from '../App/appStyles.scss'
import {StandardWidthPage} from 'common/StandardWidthPage'
import cx from 'classnames'
import {CircleSpinner} from 'common/spinners'
import {getReportCover} from 'lib/getReportCover'
import smartDate from '@qrohlf/smart-date'
import {Link} from 'react-router'
import Helmet from 'react-helmet'
import {Map, OrderedSet} from 'immutable'
import {UnderlineSelector} from 'common/UnderlineSelector'

const emptySet = OrderedSet()

export class AdminPage extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      query: Map({
        order_by: 'published_at',
        order_dir: 'DESC',
        user_id: null,
        contest_entry: null
      })
    }
  }

  componentDidMount () {
    const {currentUser} = this.props
    if (!currentUser.account_level === 11) {
      return
    }
    this.loadNextPage()
  }

  componentDidUpdate () {
    const {topics} = this.props
    const topicName = this.topicFromState()
    const nextPage = topics.getIn([topicName, 'nextPage'], 0)
    // if we haven't loaded this search yet, grab the first page of results
    if (nextPage === 0) {
      this.loadNextPage()
    }
  }

  topicFromState = () => {
    const {query} = this.state
    return query.hashCode()
  }

  loadNextPage = () => {
    const topicName = this.topicFromState()
    const {query} = this.state
    const {topics, loadReports} = this.props
    const loadPending = topics.getIn([topicName, 'loading'], false)
    if (loadPending) { return }
    const nextPage = topics.getIn([topicName, 'nextPage'], 0)
    if (nextPage === -1) {
      return
    }
    loadReports(query.filter(v => v).toJS(), {topicName, page: nextPage})
  }

  linkQuery = (param) => (value) => this.setState({query: this.state.query.set(param, value)})

  toggleFeatured = (report) => {
    const {updateReport, saveReport} = this.props
    const moderationStatus = report.moderationStatus === 'featured'
      ? 'visible'
      : 'featured'
    const newReport = {...report, moderationStatus}
    updateReport(newReport) // synchronous
    saveReport(newReport) // async
  }

  toggleSuppressed = (report) => {
    const {updateReport, saveReport} = this.props
    const moderationStatus = report.moderationStatus === 'suppressed'
      ? 'visible'
      : 'suppressed'
    const newReport = {...report, moderationStatus}
    updateReport(newReport) // synchronous
    saveReport(newReport) // async
  }

  toggleContestEntry = (report) => {
    const {updateReport, saveReport} = this.props
    const contestEntry = !report.contestEntry
    const newReport = {...report, contestEntry}
    updateReport(newReport) // synchronous
    saveReport(newReport) // async
  }

  onUidChange = (e) => {
    const newUid = parseInt(e.target.value, 10)
    const {query} = this.state
    if (!newUid || newUid === '') {
      this.setState({query: query.merge({user_id: null})})
      return
    }
    this.setState({query: query.merge({user_id: newUid})})
  }

  render () {
    const {reports, currentUser, topics} = this.props
    const {query} = this.state

    const reportIds = topics.getIn([this.topicFromState(), 'reportIds'], emptySet)
    const topicName = this.topicFromState()
    const loadPending = topics.getIn([topicName, 'loading'], false)
    const nextPage = topics.getIn([topicName, 'nextPage'], 0)

    if (!currentUser.account_level === 11) {
      return (
        <div className={styles.AdminPage}>
          <Icon i='farthingWithFlat' size={800} />
        </div>
      )
    }

    return (
      <div className={cx(appStyles.base, styles.AdminPage)}>
        <StandardWidthPage>
          <Helmet title='Ride Reports · Admin' />
          <h1>RR Admin</h1>
          <p>Showing {reportIds.size} reports {loadPending && <CircleSpinner size={17} />}</p>
          <div>
            <span className={styles.queryLabel}>order:</span>
            <UnderlineSelector
              className={styles.UnderlineSelector}
              styles={{option: styles.option, selected: styles.optionSelected}}
              selected={query.get('order_by')}
              onSelectionChanged={this.linkQuery('order_by')}
              options={Map({published_at: 'published_at', created_at: 'created_at', updated_at: 'updated_at'})} />
            <span className={styles.queryLabel}>direction:</span>
            <UnderlineSelector
              className={styles.UnderlineSelector}
              styles={{option: styles.option, selected: styles.optionSelected}}
              selected={query.get('order_dir')}
              onSelectionChanged={this.linkQuery('order_dir')}
              options={Map({DESC: 'DESC', ASC: 'ASC'})} />
            <span className={styles.queryLabel}>filter by contest entries:</span>
            <UnderlineSelector
              className={styles.UnderlineSelector}
              styles={{option: styles.option, selected: styles.optionSelected}}
              selected={query.get('contest_entry')}
              onSelectionChanged={this.linkQuery('contest_entry')}
              options={Map([[null, 'off'], [true, 'on']])} />
            <span className={styles.queryLabel}>user id:</span>
            <input type='text' value={query.get('user_id') || ''} placeholder='(all)' onChange={this.onUidChange} />
          </div>
          <div className={styles.listContainer}>
            {reportIds.map(id => reports.get(id)).map(r => <AdminReport key={r.id}
              report={r}
              toggleFeatured={this.toggleFeatured}
              toggleSuppressed={this.toggleSuppressed}
              toggleContestEntry={this.toggleContestEntry} />).toSeq()}
          </div>
          <p>
            {(nextPage !== -1)
              ? <a onClick={this.loadNextPage}>Load More {loadPending && <CircleSpinner size={17} />}</a>
              : 'end of results'
            }
          </p>
        </StandardWidthPage>
      </div>
    )
  }
}

const wordCount = (reportContent) => reportContent.blocks
  .filter(b => b.type !== 'atomic')
  .map(b => b.text.split(' ').length)
  .reduce((a, b) => a + b)

const photoCount = (reportContent) => reportContent.blocks
  .filter(b => b.type !== 'gallery')
  .map(b => (b.data && b.data.imageIds) ? b.data.imageIds.length : 0)
  .reduce((a, b) => a + b)

const AdminReport = ({report, toggleFeatured, toggleSuppressed, toggleContestEntry}) => (
  <div className={styles.AdminReport}>
    <div className={styles.imgWrap}>
      <Link to={`/ride_reports/${report.slug}`}><img className={styles.reportImg} src={getReportCover(report)} /></Link>
    </div>
    <div className={styles.reportPadded}>
      <Link to={`/ride_reports/${report.slug}`}><h2 className={styles.title}>{report.title}</h2></Link>
      <p className={styles.subtitle}>{report.subtitle || <span className={styles.italic}>no subtitle</span>}</p>
      <p><span className={styles.label}>author:</span> {report.user.name}</p>
      <p><span className={styles.label}>published:</span> {report.publishedAt && smartDate(report.publishedAt)}</p>
      <p><span className={styles.label}>words:</span> {report.reportContent ? wordCount(report.reportContent) : 0}</p>
      <p><span className={styles.label}>photos:</span> {report.reportContent ? photoCount(report.reportContent) : 0}</p>
      <p><span className={styles.label}>views:</span> {report.viewCount}</p>
    </div>
    {report.savePending
      ? <div className={cx(styles.bottomBar, styles.bbLoading)}>
        <CircleSpinner size={25} />
      </div>
      : <div className={styles.bottomBar}>
        <FeaturedIcon
          featured={report.moderationStatus === 'featured'}
          onClick={() => toggleFeatured(report)} />
        <SuppressedIcon
          suppressed={report.moderationStatus === 'suppressed'}
          onClick={() => toggleSuppressed(report)} />
        <ContestIcon
          contestEntry={report.contestEntry}
          onClick={() => toggleContestEntry(report)} />
      </div>
    }
  </div>
)

const FeaturedIcon = ({featured, onClick}) => (
  featured
    ? <Icon i='star' title='remove featured' className={styles.iconActive} size={25} onClick={onClick} />
    : <Icon i='starOutline' title='feature report' className={styles.icon} size={25} onClick={onClick} />
)

const SuppressedIcon = ({suppressed, onClick}) => (
  suppressed
    ? <Icon i='visibilityHidden' title='unhide report' className={styles.iconActive} size={25} onClick={onClick} />
    : <Icon i='visibility' title='hide report' className={styles.icon} size={25} onClick={onClick} />
)

const ContestIcon = ({contestEntry, onClick}) => (
  contestEntry
    ? <Icon i='gavel' title='remove from contest' className={styles.iconActive} size={25} onClick={onClick} />
    : <Icon i='gavel' title='add to contest' className={styles.icon} size={25} onClick={onClick} />
)

const mapStateToProps = (state) => ({
  reports: state.rideReports.reports,
  topics: state.rideReports.topics,
  currentUser: state.currentUser
})

const mapDispatchToProps = {loadReports, updateReport, saveReport}

export const AdminPageContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(AdminPage)
